import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Button, Menu, Stack, Typography } from "@mui/material";
import { LuBell } from "react-icons/lu";
import { useSelector } from "react-redux";
import { useSocketContext } from "../../../../contexts/socket";
import { ID_APP } from "../../../../utils/constants";
import {
  UPGRADE_LEVEL2,
  UPGRADE_LEVEL3,
} from "../../../../utils/socket.constants";
import { asyncGetList, asyncPutData } from "../../../../utils/httpRequests";
import {
  formatDateDisplay,
  generateLinkImage,
} from "../../../../utils/helpers";
import { useNavigate } from "react-router-dom";

function Notification() {
  const { token, profile } = useSelector((state: any) => state.auth);
  const { socket } = useSocketContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [notifies, setNotifies] = useState<any[]>([]);
  const [load, setLoad] = useState<number>(0);
  const [paginationOptions, setPaginationOptions] = useState<{
    page: number;
    totalRows: number;
  }>({
    page: 1,
    totalRows: 0,
  });

  const handleCloseMenu = () => {
    setAnchorEl(undefined);
  };

  const getNotifies = async () => {
    const condition: { [key: string]: any } = {
      page: paginationOptions.page,
      limit: 20,
      q: {
        email_receiver: profile?.email,
        "exfields.id_app": ID_APP,
        "exfields.event": {
          $in: [UPGRADE_LEVEL2, UPGRADE_LEVEL3],
        },
      },
    };
    const resp = await asyncGetList({
      apiCode: "notification",
      token,
      condition,
      withIdApp: false,
    });
    if (resp && resp?.status === 200) {
      setNotifies(resp?.data || []);
    }
  };

  const unreadNotifies = useMemo(() => {
    const result = (notifies || []).filter((noti) => !noti.read);
    return result?.length || 0;
  }, [notifies]);

  useEffect(() => {
    if (socket?.connected) {
      socket.on(UPGRADE_LEVEL2, () => {
        setLoad((prev) => prev + 1);
      });
      socket.on(UPGRADE_LEVEL3, () => {
        setLoad((prev) => prev + 1);
      });
    }
  }, [socket]);

  useEffect(() => {
    if (profile?.email) {
      getNotifies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, profile?.email]);

  return (
    <>
      {!!anchorEl && (
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleCloseMenu}
          slotProps={{
            paper: { sx: { width: "350px" } },
            root: { sx: { "& .MuiList-root": { py: 0 } } },
          }}
          disableScrollLock
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                px: 2,
                height: "42px",
                borderBottom: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">Thông báo</Typography>
            </Stack>
            {notifies?.length > 0 ? (
              <Stack
                gap={1}
                sx={{
                  width: "100%",
                  py: 1,
                  maxHeight: "300px",
                  overflow: "auto",
                }}
              >
                {notifies.map((notify) => {
                  return (
                    <Item
                      key={notify?._id}
                      data={notify}
                      onClose={handleCloseMenu}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ textAlign: "center", py: 2 }}>
                Chưa có thông báo
              </Typography>
            )}
          </Stack>
        </Menu>
      )}
      <Badge
        color="primary"
        showZero={false}
        badgeContent={unreadNotifies > 0 ? unreadNotifies : undefined}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiBadge-badge": {
            top: "10px",
            fontSize: "12px",
          },
        }}
      >
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          variant="text"
          size="small"
          endIcon={<LuBell size={20} />}
          sx={{ textTransform: "none", color: "text.primary" }}
        >
          Thông báo
        </Button>
      </Badge>
    </>
  );
}

export default Notification;

function Item({ data, onClose }: { data: any; onClose: () => void }) {
  const { token } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();

  const handleClickNotify = async () => {
    const resp = await asyncPutData({
      apiCode: "notification",
      withIdApp: false,
      id: data?._id,
      token,
      data: { read: true },
    });
    if (resp && resp?.status === 200) {
      let body;
      switch (data?.exfields?.event) {
        case UPGRADE_LEVEL2:
          body = JSON.parse(data?.exfields?.body);
          navigate("/list/participant", { state: { email: body?.email } });
          onClose();
          break;
        case UPGRADE_LEVEL3:
          body = JSON.parse(data?.exfields?.body);
          navigate("/list/participant", { state: { email: body?.email } });
          onClose();
          break;
        default:
          break;
      }
    }
  };

  return (
    <Stack
      onClick={handleClickNotify}
      direction="row"
      justifyContent="space-between"
      gap={1}
      sx={{
        px: 1,
        py: 1,
        cursor: "pointer",
        backgroundColor: data?.read ? "background.paper" : "background.default",
      }}
    >
      <Avatar
        sx={{ width: "50px", height: "50px" }}
        src={
          data?.picture_sender ? generateLinkImage(data?.picture_sender) : ""
        }
      />
      <Badge
        variant="dot"
        color="secondary"
        sx={{
          display: "block",
          flex: 1,
          "& .MuiBadge-badge": {
            top: "50%",
            right: 0,
            display: data?.read ? "none" : "",
          },
        }}
      >
        <Stack sx={{ width: "100%" }}>
          <Typography
            className="two-lines-ellipsis"
            sx={{
              width: "100%",
              fontSize: "14px",
              color: "text.secondary",
              whiteSpace: "normal",
              lineBreak: "normal",
            }}
          >
            {data?.title}
          </Typography>
          <Stack direction="row">
            <Typography sx={{ color: "secondary.main", fontSize: "12px" }}>
              {formatDateDisplay(data?.date_created, "HH:mm DD/MM/YYYY")}
            </Typography>
          </Stack>
        </Stack>
      </Badge>
    </Stack>
  );
}
